import { ApiError } from '~/api/errors';

/**
 * Jitter helpers for TanStack Query timing, so many tabs / rows / users do not fire the same
 * read on the same tick (polls, retries and stale refetches all drift apart).
 */

/** `base` scaled by a uniform random factor in `[1 - ratio, 1 + ratio]`. */
export function jitter(base: number, ratio: number): number {
  if (!Number.isFinite(base) || base <= 0) return base;
  return Math.round(base * (1 + (Math.random() * 2 - 1) * ratio));
}

/** One random factor per query instance (and ratio), held only while the query lives. */
const factors = new WeakMap<object, Map<number, number>>();

function factorFor(query: object, ratio: number): number {
  let byRatio = factors.get(query);
  if (!byRatio) {
    byRatio = new Map();
    factors.set(query, byRatio);
  }
  let factor = byRatio.get(ratio);
  if (factor === undefined) {
    factor = 1 + (Math.random() * 2 - 1) * ratio;
    byRatio.set(ratio, factor);
  }
  return factor;
}

/** Polling spread: ±15%, fixed per query instance so one query keeps a steady cadence. */
export const POLL_JITTER_RATIO = 0.15;
/** Stale-time spread: ±10%. */
export const STALE_JITTER_RATIO = 0.1;

/**
 * `base` ±15% for this query instance — return it from a `refetchInterval` function
 * (`(query) => (live ? jitteredPollMs(query, 4_000) : false)`) so open tabs drift apart.
 */
export function jitteredPollMs(query: object, base: number): number {
  return Math.round(base * factorFor(query, POLL_JITTER_RATIO));
}

/**
 * A `staleTime` of `base` ±10% (per query instance), so a long-lived read cached in many tabs
 * does not go stale — and refetch on focus — at the same moment. `Infinity` stays `Infinity`.
 */
export function jitteredStaleTime(base: number): (query: object) => number {
  return (query) =>
    Number.isFinite(base) ? Math.round(base * factorFor(query, STALE_JITTER_RATIO)) : base;
}

/** First retry waits up to this long; each further attempt doubles the ceiling. */
const RETRY_BASE_MS = 1_000;
/** Ceiling for any one retry wait. */
const RETRY_CAP_MS = 30_000;

/**
 * Exponential backoff with FULL jitter (`random(0, min(cap, base · 2^attempt))`, the AWS
 * architecture-blog rule): retries of a failed read spread across the whole window instead of
 * arriving together. Drop-in for TanStack's `retryDelay` (`attemptIndex` starts at 0).
 */
export function fullJitterRetryDelay(attemptIndex: number): number {
  const ceiling = Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** attemptIndex);
  return Math.round(Math.random() * ceiling);
}

/**
 * True for a failure worth retrying: 5xx, 408, 429, or a network / timeout error. Any other 4xx
 * fails fast (the `retryContentRead` / app-default rule).
 */
export function isTransientFailure(error: unknown): boolean {
  if (
    error instanceof ApiError &&
    error.statusCode !== null &&
    error.statusCode < 500 &&
    error.statusCode !== 408 &&
    error.statusCode !== 429
  ) {
    return false;
  }
  return true;
}
